import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Music, Mic, Lock, Crown, Loader2, Check, AlertTriangle, Play, Globe, Download, Video } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { useOutletContext } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Waveform } from "@/components/common";

const GENRES = ["pop", "hip-hop", "rap", "r&b", "rock", "country", "electronic", "edm", "synthwave", "lo-fi", "cinematic", "jazz", "metal"];
const MOODS = ["uplifting", "chill", "dark", "energetic", "romantic", "melancholic", "epic", "dreamy"];
const TEMPOS = ["slow", "medium", "upbeat", "fast"];
const VOCALS = [{ v: "male", l: "Male" }, { v: "female", l: "Female" }, { v: "mixed", l: "Any / Duet" }, { v: "instrumental", l: "Instrumental" }];
const PERFORMANCES = ["singing", "rap", "melodic rap", "r&b", "pop", "rock", "country", "hip-hop", "electronic", "instrumental"];
const STRUCTURE = ["[Intro]", "[Verse]", "[Chorus]", "[Bridge]", "[Outro]", "[Ad-lib]", "[Background vocals]"];

function SubscriptionWall() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.plans.filter((p) => p.can_generate))).catch(() => {}); }, []);
  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-4xl mx-auto" data-testid="subscription-wall">
      <div className="text-center jx-radial-bloom border border-white/10 rounded-3xl p-8 mb-8">
        <div className="w-16 h-16 mx-auto rounded-2xl jx-cta grid place-items-center mb-4"><Lock className="text-white" size={28} /></div>
        <h1 className="text-3xl font-black font-display">Unlock the AI Music Studio</h1>
        <p className="text-slate-400 mt-2 max-w-md mx-auto">Listening, following and tipping are always free. A Creator plan unlocks AI music generation, custom lyrics and Voice Studio.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.key} className="jx-card rounded-2xl p-6 flex flex-col">
            {p.badge && <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 mb-1">{p.badge}</span>}
            <h3 className="text-lg font-bold font-display">{p.name}</h3>
            <p className="text-2xl font-black mt-1">${p.price_monthly}<span className="text-sm text-slate-500 font-normal">/mo</span></p>
            <ul className="text-xs text-slate-400 space-y-1.5 mt-4 flex-1">
              {p.features.slice(0, 5).map((f, i) => <li key={i} className="flex gap-2"><Check size={13} className="text-cyan-400 shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            <button onClick={() => navigate("/plans")} className="jx-cta text-white font-semibold py-2.5 rounded-xl mt-4 text-sm">Choose {p.name}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultCard({ song }) {
  const { playSong } = usePlayer();
  const { openDownload, openLyricVideo } = useOutletContext();
  const [published, setPublished] = useState(song.visibility === "public");
  const navigate = useNavigate();
  const publish = async () => {
    try { await api.put(`/songs/${song.id}`, { visibility: published ? "private" : "public" }); setPublished(!published); toast.success(published ? "Set to private" : "Published to your profile!"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="jx-card rounded-2xl p-5 mt-6" data-testid="generation-result">
      <div className="flex items-center gap-2 text-green-400 mb-3"><Check size={18} /> <span className="font-semibold">"{song.title}" is ready!</span></div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => playSong({ ...song, stream_url: `${api.defaults.baseURL}/stream/${song.id}`, creator_name: "You" })} className="jx-cta text-white text-sm font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5"><Play size={15} /> Play</button>
        <button onClick={publish} data-testid="publish-btn" className="border border-white/15 text-sm px-4 py-2 rounded-xl flex items-center gap-1.5 hover:border-purple-500/40"><Globe size={15} /> {published ? "Published" : "Publish"}</button>
        <button onClick={() => openDownload({ ...song, creator_name: "You" })} className="border border-white/15 text-sm px-4 py-2 rounded-xl flex items-center gap-1.5 hover:border-purple-500/40"><Download size={15} /> Download</button>
        <button onClick={() => openLyricVideo({ ...song, creator_name: "You" })} className="border border-white/15 text-sm px-4 py-2 rounded-xl flex items-center gap-1.5 hover:border-cyan-500/40"><Video size={15} /> Lyric Video</button>
        <button onClick={() => navigate("/library")} className="text-sm px-4 py-2 rounded-xl text-slate-400 hover:text-white ml-auto">Go to Library →</button>
      </div>
    </div>
  );
}

export default function Studio() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("prompt");
  const [form, setForm] = useState({ title: "", genre: "pop", style: "", mood: "uplifting", tempo: "medium", vocal: "mixed", performance: "singing", lyrics: "", duration: 60 });
  const [quote, setQuote] = useState(null);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);
  const lyricsRef = useRef(null);

  const canGen = user && user.plan_key !== "free" && ["active", "canceled"].includes(user.subscription_status);
  useEffect(() => {
    if (!canGen) return;
    api.get("/studio/quote", { params: { mode: "text", seconds: form.duration } }).then((r) => setQuote(r.data)).catch(() => {});
  }, [form.duration, canGen, user]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  if (user === null) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-purple-400" /></div>;
  if (!user) {
    return (
      <div className="px-6 pt-16 text-center max-w-md mx-auto">
        <Sparkles className="mx-auto text-purple-400 mb-3" size={40} />
        <h1 className="text-2xl font-bold font-display">Sign in to create</h1>
        <p className="text-slate-400 mt-2">Create a free JixxeoX account to start making music.</p>
        <button onClick={() => navigate("/login")} className="jx-cta text-white font-semibold px-6 py-3 rounded-xl mt-5">Sign In / Register</button>
      </div>
    );
  }
  const canGenerate = user.plan_key !== "free" && ["active", "canceled"].includes(user.subscription_status);
  if (!canGenerate) return <SubscriptionWall />;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const insertStructure = (tag) => set("lyrics", (form.lyrics ? form.lyrics + "\n" : "") + tag + "\n");

  const generate = async () => {
    if (!form.title.trim()) return toast.error("Give your song a title");
    setGenerating(true); setResult(null); setJob({ status: "processing" });
    try {
      const { data } = await api.post("/songs/generate", { ...form, mode: "text", duration: Number(form.duration) });
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/generation/${data.job_id}`);
          setJob(s.job);
          if (s.job.status === "ready") { clearInterval(pollRef.current); setResult(s.song); setGenerating(false); refresh(); api.get("/studio/quote", { params: { mode: "text", seconds: form.duration } }).then((r) => setQuote(r.data)).catch(() => {}); }
          else if (s.job.status === "failed") { clearInterval(pollRef.current); setGenerating(false); refresh(); toast.error(s.job.error || "Generation failed"); }
        } catch { clearInterval(pollRef.current); setGenerating(false); }
      }, 3000);
    } catch (e) {
      setGenerating(false); setJob(null);
      const reason = e.response?.data?.detail?.reason;
      if (reason === "insufficient_credits") toast.error(e.response.data.detail.message || "Not enough credits this period.");
      else if (reason === "limit_reached") toast.error("You've reached your plan's generation limit this period.");
      else toast.error(errMsg(e));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold font-display flex items-center gap-2"><Sparkles className="text-purple-400" /> AI Music Studio</h1>
        <div className="flex items-center gap-3">
          {quote && (
            <span className="text-xs font-mono px-3 py-1.5 rounded-full bg-white/5 border border-white/10" data-testid="credit-balance">
              <span className="text-cyan-400 font-semibold">{Math.max(0, quote.allowance - quote.credits_used)}</span>
              <span className="text-slate-500"> / {quote.allowance} credits</span>
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-5 border-b border-white/10">
        {[["prompt", "Text-to-Music"], ["lyrics", "Custom Lyrics"], ["structure", "Song Structure"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`studio-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-purple-500 text-white" : "border-transparent text-slate-400"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-4">
        <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Song title" data-testid="studio-title"
          className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50" />

        {tab === "prompt" && (
          <>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Genre</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {GENRES.map((g) => <button key={g} onClick={() => set("genre", g)} className={`px-3 py-1.5 rounded-full text-xs capitalize border ${form.genre === g ? "jx-cta text-white border-transparent" : "border-white/10 text-slate-300"}`}>{g}</button>)}
              </div>
            </div>
            <input value={form.style} onChange={(e) => set("style", e.target.value)} placeholder="Style keywords (e.g. 80s, dreamy synths, guitar)" data-testid="studio-style"
              className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Mood</label>
                <select value={form.mood} onChange={(e) => set("mood", e.target.value)} className="w-full mt-2 bg-[#121217] border border-white/10 rounded-xl p-2.5 text-sm capitalize">{MOODS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Energy</label>
                <select value={form.tempo} onChange={(e) => set("tempo", e.target.value)} className="w-full mt-2 bg-[#121217] border border-white/10 rounded-xl p-2.5 text-sm capitalize">{TEMPOS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              </div>
            </div>
          </>
        )}

        {(tab === "lyrics" || tab === "structure") && (
          <>
            {tab === "structure" && (
              <div className="flex flex-wrap gap-2">
                {STRUCTURE.map((s) => <button key={s} onClick={() => insertStructure(s)} className="px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300 hover:border-purple-500/40">{s}</button>)}
              </div>
            )}
            <textarea ref={lyricsRef} value={form.lyrics} onChange={(e) => set("lyrics", e.target.value)} rows={8} data-testid="studio-lyrics"
              placeholder={"Enter your own lyrics.\nUse [Verse], [Chorus] etc. to structure the song.\nLeave blank for an instrumental."}
              className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50 font-mono" />
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><AlertTriangle size={13} /> JixxeoX never invents your lyrics — type or paste the words you want performed.</p>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Vocals</label>
            <select value={form.vocal} onChange={(e) => set("vocal", e.target.value)} data-testid="studio-vocal" className="w-full mt-2 bg-[#121217] border border-white/10 rounded-xl p-2.5 text-sm">{VOCALS.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}</select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Performance</label>
            <select value={form.performance} onChange={(e) => set("performance", e.target.value)} className="w-full mt-2 bg-[#121217] border border-white/10 rounded-xl p-2.5 text-sm capitalize">{PERFORMANCES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
          </div>
        </div>

        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-purple-400">Length: {form.duration}s {quote && <span className="text-slate-500 normal-case">(max {quote.max_song_seconds}s on your plan)</span>}</label>
          <input type="range" min="15" max={quote?.max_song_seconds || 180} value={Math.min(form.duration, quote?.max_song_seconds || 180)} onChange={(e) => set("duration", Number(e.target.value))} className="w-full mt-2 accent-purple-500" />
        </div>

        {quote && (
          <div className="flex items-center justify-between text-sm bg-black/20 rounded-xl px-4 py-3 border border-white/10" data-testid="credit-estimate">
            <span className="text-slate-400">This track costs</span>
            <span className="font-semibold"><span className="text-cyan-400">{quote.credits_needed} credits</span> <span className="text-slate-500">(≈${quote.member_price.toFixed(2)})</span></span>
          </div>
        )}

        <button onClick={generate} disabled={generating} data-testid="generate-btn" className="w-full jx-cta text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
          {generating ? <><Loader2 className="animate-spin" size={18} /> Generating…</> : <><Sparkles size={18} /> Generate Song</>}
        </button>

        {generating && job?.status === "processing" && (
          <div className="jx-card rounded-2xl p-6 text-center">
            <Waveform bars={20} className="h-10 justify-center mb-3" />
            <p className="font-semibold">Composing your track with AI…</p>
            <p className="text-sm text-slate-400 mt-1">This usually takes 30–90 seconds. You'll get a notification when it's ready.</p>
          </div>
        )}
        {result && <ResultCard song={result} />}
      </div>

    </div>
  );
}
